import { Intent } from "@blueprintjs/core";
import React, { memo, useMemo } from "react";
import styled from "styled-components";

import {
  HexButton,
  HexMenu,
  HexMenuDivider,
  HexPopover,
} from "../../hex-components/index.js";
import { HotKeys } from "../../util/hotkeys.js";
import { TestSelectors } from "../../util/testSelectors.js";
import { HotkeyHelperTooltip } from "../common/HotkeyHelperTooltip.js";
import { AddIcon, ProjectIcon, ThreadIcon } from "../icons/CustomIcons.js";

import {
  CreateCollectionMenuItem,
  CreateComponentMenuItem,
  CreateExplorationMenuItem,
  CreateRComponentMenuItem,
  CreateRProjectMenuItem,
  ImportComponentMenuItem,
  ImportProjectMenuItem,
} from "./create-items/CreateMenuItems";
import {
  useCreateProjectButton,
  useCreateThreadButton,
} from "./create-items/useCreateAssets";

interface QuickCreateButtonsProps {
  location: "home_sidebar" | "popover_sidebar";
}

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: stretch;
`;

const ProjectButton = styled(HexButton)`
  width: 100%;
`;

const ThreadButton = styled(HexButton)`
  width: 100%;
`;

const MoreButton = styled(HexButton)`
  flex: 0;
`;

export const QuickCreateButtons = memo(function QuickCreateButtons({
  location,
}: QuickCreateButtonsProps) {
  const { canCreateNewThread, createNewThread } = useCreateThreadButton({
    location,
  });
  const { createNewProject, newProjectDisabled } = useCreateProjectButton({
    location,
  });

  const dropdownMenuContents = useMemo(() => {
    const placement = "right-start";
    return (
      <HexMenu>
        <CreateComponentMenuItem location={location} placement={placement} />
        <CreateExplorationMenuItem location={location} placement={placement} />
        <CreateCollectionMenuItem location={location} placement={placement} />
        <HexMenuDivider />
        <CreateRProjectMenuItem location={location} />
        <CreateRComponentMenuItem location={location} placement={placement} />
        <HexMenuDivider />
        <ImportProjectMenuItem location={location} />
        <ImportComponentMenuItem location={location} placement={placement} />
      </HexMenu>
    );
  }, [location]);

  return (
    <ButtonsContainer>
      <HotkeyHelperTooltip
        combo={HotKeys.NEW_PROJECT}
        css="flex: 1;"
        disabled={newProjectDisabled}
        hotkeyName="New project"
        placement="bottom"
      >
        <ProjectButton
          data-test={TestSelectors.CREATE_PROJECT}
          disabled={newProjectDisabled}
          fill={true}
          icon={<ProjectIcon />}
          intent={Intent.NONE}
          onClick={createNewProject}
        >
          Project
        </ProjectButton>
      </HotkeyHelperTooltip>
      <div css="flex: 1;">
        <ThreadButton
          data-test={TestSelectors.CREATE_THREAD}
          disabled={!canCreateNewThread}
          fill={true}
          icon={<ThreadIcon />}
          intent={Intent.NONE}
          onClick={createNewThread}
        >
          Thread
        </ThreadButton>
      </div>
      <HexPopover
        content={dropdownMenuContents}
        minimal={true}
        placement="bottom-start"
      >
        <MoreButton
          data-test={TestSelectors.NEW_PROJECT_DROPDOWN}
          fill={true}
          icon={<AddIcon />}
          intent={Intent.NONE}
        />
      </HexPopover>
    </ButtonsContainer>
  );
});
